import * as TabsPrimitive from "@radix-ui/react-tabs";
import { type VariantProps, cva } from "class-variance-authority";
import { m } from "framer-motion";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
  useState,
} from "react";
import { cn } from "../cn";
import { springSoft, useMotionTransition } from "../motion";
import { useSlidingIndicator } from "./useSlidingIndicator";

export type TabsVariant = "pill" | "underline";

/**
 * The tab strip. "pill" keeps the rounded, muted container; "underline" is a
 * full-width bare strip with a static border-b track under the whole list
 * (design-lab's `flex w-full border-b border-border/70`), and the animated
 * rule under the active tab rides on top of that track.
 */
export const tabsListVariants = cva("relative items-center", {
  variants: {
    variant: {
      pill: "inline-flex min-w-max gap-1 rounded-full border border-border/70 bg-muted/60 p-1",
      underline: "flex w-full gap-6 border-b border-border/70",
    },
  },
  defaultVariants: { variant: "pill" },
});

/**
 * The sliding indicator. In "underline" the rule is anchored to the list's
 * bottom edge (`bottom-0 h-0.5`) and only `x`/`width` are animated — the
 * measured trigger bottom never enters it, so no height is ever animated
 * (the reduced-motion contract) while `h-0.5` always has paintable height.
 */
export const tabsIndicatorVariants = cva(
  "pointer-events-none absolute left-0",
  {
    variants: {
      variant: {
        pill: "top-0 rounded-full bg-surface shadow-sm ring-1 ring-border/60",
        underline: "bottom-0 h-0.5 bg-primary",
      },
    },
    defaultVariants: { variant: "pill" },
  },
);

/**
 * The shared base keeps Radix's active-text treatment
 * (`data-[state=active]:text-foreground`) in both variants, so the active tab
 * is distinguishable by more than the underline's colour. Both variants size
 * the trigger to its label (`shrink-0` on the base, not just pill): the
 * full-width underline list's `gap-6` provides the spacing, and without
 * `shrink-0` a narrow viewport would compress triggers below their label
 * width instead of letting the list's own `overflow-x-auto` wrapper scroll,
 * which would desync the measured indicator from the (now-wrapped) label.
 */
export const tabsTriggerVariants = cva(
  "relative z-10 inline-flex h-9 shrink-0 items-center justify-center px-4 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-40 data-[state=active]:text-foreground",
  {
    variants: {
      variant: {
        pill: "rounded-full",
        underline: "",
      },
    },
    defaultVariants: { variant: "pill" },
  },
);

interface TabsContextValue {
  readonly activeValue: string;
  readonly variant: TabsVariant;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs subcomponents must be used within <Tabs>");
  }
  return context;
}

export interface TabsProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    VariantProps<typeof tabsListVariants> {
  /**
   * Visual treatment. "pill" (default) slides a rounded highlight behind the
   * active tab inside a muted container; "underline" draws a short rule under
   * the active tab only, for a tab strip that heads a page section.
   */
  readonly variant?: TabsVariant;
}

/**
 * Accessible Radix-backed tabs with controlled and uncontrolled selection.
 * Routing remains a consumer concern: route search state can be connected via
 * `value` and `onValueChange` without coupling this primitive to the router.
 */
export const Tabs = forwardRef<
  ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(function Tabs(
  {
    value,
    defaultValue,
    onValueChange,
    children,
    className,
    variant = "pill",
    ...props
  },
  ref,
) {
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue ?? "",
  );
  const activeValue = value ?? uncontrolledValue;

  return (
    <TabsContext.Provider value={{ activeValue, variant }}>
      <TabsPrimitive.Root
        {...props}
        ref={ref}
        value={value}
        defaultValue={defaultValue}
        onValueChange={(nextValue) => {
          if (value === undefined) {
            setUncontrolledValue(nextValue);
          }
          onValueChange?.(nextValue);
        }}
        className={className}
      >
        {children}
      </TabsPrimitive.Root>
    </TabsContext.Provider>
  );
});

Tabs.displayName = "Tabs";

export interface TabsListProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  /** Class applied to the horizontal-overflow wrapper. */
  readonly wrapperClassName?: string;
}

/** A horizontally scrollable tab list with a measured sliding indicator. */
export function TabsList({
  children,
  className,
  wrapperClassName,
  ...props
}: TabsListProps): ReactNode {
  const { activeValue, variant } = useTabsContext();
  const { containerRef, indicator } = useSlidingIndicator<HTMLDivElement>(
    activeValue,
    '[data-state="active"]',
  );
  const indicatorTransition = useMotionTransition(springSoft);

  return (
    <div className={cn("max-w-full overflow-x-auto", wrapperClassName)}>
      <TabsPrimitive.List
        {...props}
        ref={containerRef}
        className={cn(tabsListVariants({ variant }), className)}
      >
        {indicator ? (
          <m.span
            aria-hidden="true"
            className={tabsIndicatorVariants({ variant })}
            initial={false}
            animate={
              variant === "pill"
                ? {
                    x: indicator.left,
                    y: indicator.top,
                    width: indicator.width,
                    height: indicator.height,
                  }
                : {
                    x: indicator.left,
                    width: indicator.width,
                  }
            }
            transition={indicatorTransition}
          />
        ) : null}
        {children}
      </TabsPrimitive.List>
    </div>
  );
}

export type TabsTriggerProps = ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
>;

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(function TabsTrigger({ children, className, ...props }, ref) {
  const { variant } = useTabsContext();

  return (
    <TabsPrimitive.Trigger
      {...props}
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
});

TabsTrigger.displayName = "TabsTrigger";

export type TabsContentProps = Omit<
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>,
  "forceMount" | "hidden"
> & {
  /** Keep children mounted (hidden) while inactive, so local state survives tab switches. */
  readonly keepMounted?: boolean;
};

/**
 * Keeps each empty panel node mounted so `aria-controls` always resolves while
 * unmounting inactive children to avoid hidden queries or expensive effects.
 */
export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(function TabsContent(
  { value, children, className, keepMounted = false, ...props },
  ref,
) {
  const { activeValue } = useTabsContext();
  const active = activeValue === value;

  return (
    <TabsPrimitive.Content
      {...props}
      ref={ref}
      forceMount
      value={value}
      hidden={!active}
      className={cn(
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
    >
      {active || keepMounted ? children : null}
    </TabsPrimitive.Content>
  );
});

TabsContent.displayName = "TabsContent";
